const { CALCULATION_VERSION } = require("../constants/calculationVersion");

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function normalizeCurrencyValue(value, fallback = "DKK") {
  return value === "SEK" || value === "DKK" ? value : fallback;
}

function normalizeRowType(row, fallbackType) {
  if (row?.type === "material" || row?.type === "labour" || row?.type === "extra") {
    return row.type;
  }
  return fallbackType;
}

function deriveQuantity(row, type) {
  if (row?.quantity != null) return Number(row.quantity);
  if (row?.qty != null) return Number(row.qty);
  if (type === "labour" && row?.hours != null) return Number(row.hours);
  return 0;
}

function deriveUnit(row, type) {
  if (row?.unit) return row.unit;
  return type === "labour" ? "hour" : "pcs";
}

function deriveUnitPrice(row, type) {
  if (row?.unitPrice != null) return Number(row.unitPrice);
  if (row?.price != null) return Number(row.price);
  if (type === "labour" && row?.hourlyRate != null) return Number(row.hourlyRate);
  return 0;
}

function deriveFlags(row, hasInvalidPrice) {
  return {
    manuallyEdited: !!(row?.flags?.manuallyEdited || row?.manuallyEdited),
    aiAdjusted: !!(row?.flags?.aiAdjusted || row?.aiAdjusted),
    reviewRequired:
      !!(row?.flags?.reviewRequired || row?.meta?.review || hasInvalidPrice),
    invalidPrice: hasInvalidPrice,
  };
}

function ensureMeta(row, index, fallbackSourceType) {
  const sourceType = row?.meta?.sourceType || fallbackSourceType;
  if (!sourceType) {
    throw new Error(`Row at index ${index} missing sourceType`);
  }

  return {
    ...row.meta,
    sourceType,
    sourceLabel:
      row?.meta?.sourceLabel ||
      row?.label ||
      `${fallbackSourceType} ${index + 1}`,
    sourceIndex:
      row?.meta?.sourceIndex != null
        ? Number(row.meta.sourceIndex)
        : index,
    calculationVersion:
      row?.meta?.calculationVersion || CALCULATION_VERSION,
  };
}

function normalizeRow(row, index, fallbackType, fallbackSourceType, fallbackCurrency) {
  const type = normalizeRowType(row, fallbackType);

  const quantity = round2(deriveQuantity(row, type));
  const unit = deriveUnit(row, type);

  let unitPrice = round2(deriveUnitPrice(row, type));
  const hasInvalidPrice = !Number.isFinite(unitPrice) || unitPrice < 0;

  if (hasInvalidPrice) {
    console.warn("❌ Invalid unitPrice detected:", {
      label: row?.label,
      unitPrice,
      type,
    });
    unitPrice = 0;
  }

  // ALWAYS recompute total (no stale values)
  const total = round2(quantity * unitPrice);

  const currency = normalizeCurrencyValue(row?.currency, fallbackCurrency);
  const meta = ensureMeta(row, index, fallbackSourceType);
  const flags = deriveFlags(row, hasInvalidPrice);

  return {
    ...row,
    id: row?.id || `row_${type}_${meta.sourceType}_${meta.sourceIndex}_${index}`,
    type,
    label: row?.label || `Unnamed ${type}`,
    quantity,
    unit,
    unitPrice,
    total,
    currency,
    meta,
    flags,
  };
}

function normalizeRows(rows, fallbackType, fallbackSourceType, fallbackCurrency) {
  return (Array.isArray(rows) ? rows : []).map((row, index) =>
    normalizeRow(row, index, fallbackType, fallbackSourceType, fallbackCurrency)
  );
}

function sumTotals(rows) {
  return round2(rows.reduce((sum, r) => sum + Number(r.total || 0), 0));
}

function normalizeSummary(summary, materials, labour, extras, currency) {
  const materialsSubtotal = sumTotals(materials);
  const labourSubtotal = sumTotals(labour);
  const extrasSubtotal = sumTotals(extras);

  const baseCost = round2(materialsSubtotal + labourSubtotal + extrasSubtotal);

  const marginPercent = round2(Number(summary?.marginPercent ?? 0));
  const marginAmount = round2(baseCost * (marginPercent / 100));

  const subtotal = round2(baseCost + marginAmount);

  const vatPercent = round2(Number(summary?.vatPercent ?? 25));
  const vatAmount = round2(subtotal * (vatPercent / 100));

  const total = round2(subtotal + vatAmount);

  return {
    materialsSubtotal,
    labourSubtotal,
    extrasSubtotal,
    baseCost,
    marginPercent,
    marginAmount,
    subtotal,
    vatPercent,
    vatAmount,
    vat: vatAmount,
    total,
    currency,
  };
}

function normalizeEstimateMeta(meta, currency) {
  return {
    ...meta,
    calculationVersion: meta?.calculationVersion || CALCULATION_VERSION,
    currency: normalizeCurrencyValue(meta?.currency, currency),
  };
}

function normalizeEstimate(estimate = {}) {
  const fallbackCurrency = normalizeCurrencyValue(
    estimate?.meta?.currency || estimate?.summary?.currency,
    "DKK"
  );

  const fallbackSourceType = estimate?.meta?.sourceType || "custom";

  const materials = normalizeRows(
    estimate.materials,
    "material",
    fallbackSourceType,
    fallbackCurrency
  );

  const labour = normalizeRows(
    estimate.labour,
    "labour",
    fallbackSourceType,
    fallbackCurrency
  );

  const extras = normalizeRows(
    estimate.extras,
    "extra",
    fallbackSourceType,
    fallbackCurrency
  );

  const summary = normalizeSummary(
    estimate.summary,
    materials,
    labour,
    extras,
    fallbackCurrency
  );

  const meta = normalizeEstimateMeta(estimate.meta || {}, fallbackCurrency);

  return {
    ...estimate,
    materials,
    labour,
    extras,
    summary,
    meta,
  };
}

module.exports = {
  normalizeEstimate,
  normalizeRow,
};